// Single source of truth for the Aspired AI vs Sintra AI vs ChatGPT comparison.
// Hardcoded TS const — Sintra/ChatGPT capabilities don't change weekly,
// and a deploy is the right edit cadence for marketing copy.

export type CellValue =
  | { type: 'check'; label?: string }
  | { type: 'cross'; label?: string }
  | { type: 'partial'; label: string }
  | { type: 'text'; value: string }

export interface ComparisonRow {
  feature: string
  aspired: CellValue
  sintra: CellValue
  chatgpt: CellValue
  highlight?: boolean
}

export interface ComparisonCategory {
  label: string
  rows: ComparisonRow[]
}

export const comparisonCategories: ComparisonCategory[] = [
  {
    label: 'Agents & Specialization',
    rows: [
      { feature: 'Specialized AI agents', aspired: { type: 'text', value: '58' }, sintra: { type: 'text', value: '12' }, chatgpt: { type: 'text', value: '1' }, highlight: true },
      { feature: 'Industry-trained agents', aspired: { type: 'text', value: '48' }, sintra: { type: 'text', value: '0' }, chatgpt: { type: 'text', value: '0' }, highlight: true },
      { feature: 'Industries covered', aspired: { type: 'text', value: '12' }, sintra: { type: 'text', value: '0' }, chatgpt: { type: 'text', value: '0' }, highlight: true },
      { feature: 'Custom agent personality', aspired: { type: 'check' }, sintra: { type: 'partial', label: 'Limited' }, chatgpt: { type: 'cross' } },
      { feature: 'Versioned agent prompts', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
    ],
  },
  {
    label: 'Knowledge & Memory',
    rows: [
      { feature: 'Per-agent knowledge base', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'Global knowledge base', aspired: { type: 'check' }, sintra: { type: 'check' }, chatgpt: { type: 'partial', label: 'Memory only' } },
      { feature: 'File uploads (PDF, DOCX, TXT)', aspired: { type: 'check' }, sintra: { type: 'check' }, chatgpt: { type: 'check' } },
      { feature: 'Image uploads to memory', aspired: { type: 'check' }, sintra: { type: 'partial', label: 'Limited' }, chatgpt: { type: 'check' } },
      { feature: 'Auto-save from chat', aspired: { type: 'check' }, sintra: { type: 'check' }, chatgpt: { type: 'partial', label: 'Partial' } },
      { feature: 'Conversational brand kit setup', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'Industry knowledge baked in', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
    ],
  },
  {
    label: 'Collaboration',
    rows: [
      { feature: 'Agent-to-agent @mention handoffs', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'Multi-agent in one conversation', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'Continuation handling for long tasks', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
    ],
  },
  {
    label: 'Content & Marketing Tools',
    rows: [
      { feature: 'AI image generation', aspired: { type: 'check' }, sintra: { type: 'check' }, chatgpt: { type: 'check' } },
      { feature: 'Brand-aware image enrichment', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'Social post drafting', aspired: { type: 'check' }, sintra: { type: 'check' }, chatgpt: { type: 'cross' } },
      { feature: 'Social post scheduling', aspired: { type: 'check' }, sintra: { type: 'check' }, chatgpt: { type: 'cross' } },
      { feature: 'Facebook integration', aspired: { type: 'check' }, sintra: { type: 'check' }, chatgpt: { type: 'cross' } },
      { feature: 'Instagram integration', aspired: { type: 'check' }, sintra: { type: 'check' }, chatgpt: { type: 'cross' } },
      { feature: 'LinkedIn integration', aspired: { type: 'partial', label: 'Beta' }, sintra: { type: 'check' }, chatgpt: { type: 'cross' } },
      { feature: 'Content calendar view', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'Active content theme system', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
    ],
  },
  {
    label: 'Platform',
    rows: [
      { feature: 'Dark mode', aspired: { type: 'check' }, sintra: { type: 'check' }, chatgpt: { type: 'check' } },
      { feature: 'Mobile responsive', aspired: { type: 'check' }, sintra: { type: 'check' }, chatgpt: { type: 'check' } },
      { feature: 'Native mobile app', aspired: { type: 'partial', label: 'Coming soon' }, sintra: { type: 'partial', label: 'iOS only' }, chatgpt: { type: 'check' } },
      { feature: 'SEO-optimized public pages', aspired: { type: 'check' }, sintra: { type: 'partial', label: 'Basic' }, chatgpt: { type: 'cross' } },
    ],
  },
  {
    label: 'Business Features',
    rows: [
      { feature: 'Founder pricing locked for life', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'White-glove onboarding', aspired: { type: 'text', value: '$99 one-time' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'Agency / reseller tier', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'Multi-seat workspaces', aspired: { type: 'check' }, sintra: { type: 'partial', label: 'Add-on' }, chatgpt: { type: 'partial', label: 'Teams plan' } },
      { feature: 'Referral credits', aspired: { type: 'check' }, sintra: { type: 'partial', label: 'Free month' }, chatgpt: { type: 'cross' } },
      { feature: 'Beta program', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'Account credits / coupons', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
    ],
  },
  {
    label: 'Trust & Reliability',
    rows: [
      { feature: 'Versioned governing rules', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'Automated bug reporting', aspired: { type: 'check', label: 'Rex agent' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
      { feature: 'Subscription change protection', aspired: { type: 'check', label: 'Severity-based' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
    ],
  },
]

// Top 5 highest-impact rows for the homepage compact teaser.
export const compactRows: ComparisonRow[] = [
  { feature: 'Specialized AI agents', aspired: { type: 'text', value: '58' }, sintra: { type: 'text', value: '12' }, chatgpt: { type: 'text', value: '1' }, highlight: true },
  { feature: 'Industry-trained agents', aspired: { type: 'text', value: '48' }, sintra: { type: 'text', value: '0' }, chatgpt: { type: 'text', value: '0' }, highlight: true },
  { feature: 'Per-agent knowledge base', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
  { feature: 'Agent-to-agent collaboration', aspired: { type: 'check' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
  { feature: 'White-glove onboarding', aspired: { type: 'text', value: '$99 one-time' }, sintra: { type: 'cross' }, chatgpt: { type: 'cross' } },
]
